import { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    Button,
    Grid,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    Stack,
    Snackbar,
    Alert,
} from '@mui/material';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { Breadcrumb } from 'components';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PublishIcon from '@mui/icons-material/Publish';
import { getAllReleaseNotesHistoryApi, addReleaseNotesApi, getReleaseNotesIdApi, updateReleaseNotesApi, deleteReleaseNotesApi } from 'services';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import moment from 'moment';
import Editor from './Editor';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

export const ReleaseNotesHistory = () => {
    const [paginationModel, setPaginationModel] = useState({
        page: 1,
        pageSize: 10,
        total: 0,
        data: [],
        isLoading: false,
    });
    const [releaseNotesModel, setReleaseNotesModel] = useState({
        userType: null,
        title: "",
        message: "",
        versionNo: "",
        releaseDate: null,
        labelName: "",
        notes: "",
        id: ""
    });
    const [editorOpen, setEditorOpen] = useState(false);
    const [publishNotificationStatus, setPublishNotificationStatus] = useState(false);
    const [saveNotificationStatus, setSaveNotificationStatus] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [successRequest, setSuccessRequest] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [validationErrors, setValidationErrors] = useState({
        userType: false,
        title: false,
        message: false,
        versionNo: false,
        releaseDate: false,
    });

    const labelOptions = [
        'Feature Update',
        'Bug Fix',
        'Performance Improvement',
        'UI Enhancement',
        'Security Patch',
    ];

    const getUserTypeText = (userType: number) => {
        switch (userType) {
            case 1:
                return 'Internal User';
            case 2:
                return 'External User';
            case -1:
                return 'Both';
            default:
                return '';
        }
    };

    const columns = [
        {
            field: 'versionNumber',
            headerName: 'Version No',
            minWidth: 100,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title={params.row.versionNumber} arrow>
                        <span>{params.row.versionNumber}</span>
                    </Tooltip>
                );
            }
        },
        {
            field: 'labels',
            headerName: 'Label Name',
            minWidth: 300,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title={params.row.labels} arrow>
                        <span>{params.row.labels}</span>
                    </Tooltip>
                );
            }
        },
        {
            field: 'releaseDate',
            headerName: 'Date of Release',
            minWidth: 150,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title={moment(params.row.releaseDate).format("DD/MM/YYYY")} arrow>
                        <span>{moment(params.row.releaseDate).format("DD/MM/YYYY")}</span>
                    </Tooltip>
                );
            }
        },
        {
            field: 'userType',
            headerName: 'User Type',
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title={getUserTypeText(params.row.userType)} arrow>
                        <span>{getUserTypeText(params.row.userType)}</span>
                    </Tooltip>
                );
            }
        },
        {
            field: 'title',
            headerName: 'Title',
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title={params.row.title} arrow>
                        <span>{params.row.title}</span>
                    </Tooltip>
                );
            }
        },
        {
            field: 'message',
            headerName: 'Message',
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title={params.row.message} arrow>
                        <span>{params.row.message}</span>
                    </Tooltip>
                );
            }
        },
        {
            field: 'publishedDate',
            headerName: 'Published Date',
            minWidth: 150,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title={moment(params.row.publishedDate).format("DD/MM/YYYY")} arrow>
                        <span>{moment(params.row.publishedDate).format("DD/MM/YYYY")}</span>
                    </Tooltip>
                );
            }
        },
        {
            field: 'action',
            headerName: 'Action',
            minWidth: 80,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <>
                     <Tooltip title="Edit Release Notes" arrow>
                        <IconButton size="small" onClick={() => getReleaseNotesId(params.row.id)}>
                            {!params.row.isPublished ? <NoteAltIcon color="primary" /> : null}
                        </IconButton>
                    </Tooltip>
                     <Tooltip title="Delete" arrow>
                        <IconButton size="small" onClick={() => deleteReleaseNotes(params.row.id)}>
                            {!params.row.isPublished ? <DeleteOutlineOutlinedIcon color="error" /> : null}
                        </IconButton>
                    </Tooltip>
                    </>                   
                );
            },
        }
    ];

    const getReleaseNotesId = (id: any) => {
        getReleaseNotesIdApi(id).then((res: any) => {
            if (res.status === 200) {
                setEditorOpen(true);
                setReleaseNotesModel({
                    userType: res.data.userType,
                    title: res.data.title,
                    message: res.data.message,
                    versionNo: res.data.versionNumber,
                    releaseDate: moment(res.data.releaseDate).format('YYYY-MM-DD'),
                    labelName: res.data.labels,
                    notes: res.data.notes,
                    id: res.data.id
                });
            } else {
                setError(true);
                setErrorMessage(res.data);
            }
        })
    }

    const deleteReleaseNotes = (id: any) => {
        deleteReleaseNotesApi(id).then((res: any) => {
            if (res.status === 200) {
                setSuccessRequest(true);
                setSuccessMessage(res.data);
                getAllReleaseNotes();
            } else {
                setError(true);
                setErrorMessage(res.data);
            }
        })
    }

    const onClickEditor = () => {
        setEditorOpen(true);
    };

    const handleEditorClose = () => {
        setEditorOpen(false);
        setReleaseNotesModel({
            userType: null,
            title: "",
            message: "",
            versionNo: "",
            releaseDate: null,
            labelName: "",
            notes: "",
            id: ""
        });
        setValidationErrors({
            userType: false,
            title: false,
            message: false,
            versionNo: false,
            releaseDate: false,
        });
        setError(false);
    };

    useEffect(() => {
        getAllReleaseNotes();
    }, [])

    const getAllReleaseNotes = () => {
        getAllReleaseNotesHistoryApi().then((res: any) => {
            if (res.status === 200) {
                if (res.data.releaseNotes.length > 0) {
                    setPaginationModel(old => ({ ...old, data: res.data.releaseNotes, total: res.data.releaseNotesCount }));
                } else {
                    setPaginationModel(old => ({ ...old, data: [], total: 0 }));
                }
            } else {
                setError(true);
                setErrorMessage(res.data);
            }
        })
    }

    const handleAddReleaseNotes = (type: any) => {
        const errors = {
            userType: !releaseNotesModel.userType,
            title: !releaseNotesModel.title.trim(),
            message: !releaseNotesModel.message.trim(),
            versionNo: !releaseNotesModel.versionNo.trim(),
            releaseDate: !releaseNotesModel.releaseDate || releaseNotesModel.releaseDate === "Invalid date",
        };

        setValidationErrors(errors);

        if (Object.values(errors).some((error) => error)) {
            setError(true);
            setErrorMessage("Please fill in all required fields before submitting.");
            return;
        }

        let data = {
            "userType": releaseNotesModel.userType,
            "versionNumber": releaseNotesModel.versionNo,
            "labels": releaseNotesModel.labelName,
            "title": releaseNotesModel.title,
            "message": releaseNotesModel.message,
            "notes": releaseNotesModel.notes,
            "releaseDate": releaseNotesModel.releaseDate,
            "isPublished": type === "publish" ? true : false,
            "publishedDate": moment().format('YYYY-MM-DD')
        };

        if (releaseNotesModel.id !== "") {
            data["id"] = releaseNotesModel.id;
        }

        let apiCall = releaseNotesModel.id !== "" ? updateReleaseNotesApi : addReleaseNotesApi;
        apiCall(data).then((res: any) => {
            if (res.status === 200) {
                setEditorOpen(false);
                setReleaseNotesModel({
                    userType: null,
                    title: "",
                    message: "",
                    versionNo: "",
                    releaseDate: null,
                    labelName: "",
                    notes: "",
                    id: ""
                });
                getAllReleaseNotes();
                if (type === "publish") {
                    setPublishNotificationStatus(true);
                } else {
                    setSaveNotificationStatus(true);
                }
            } else {
                setError(true);
                setErrorMessage(res.data);
            }
        });
    };

    let nav: any = [
        { name: 'Home', path: '/home' },
        { name: 'Admin', path: '/home/admin' },
        { name: 'Release Notes History', path: '/home/releaseNotesHistory' },
    ];

    return (
        <div style={{ height: "auto" }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '16px 0',
                    cursor: 'pointer',
                }}
            >
                <Breadcrumb navList={nav} />
            </div>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                }}
            >
                <Typography variant="h6" gutterBottom align="left">
                    Release Notes History
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={12}>
                        <div style={{ textAlign: 'right', paddingBottom: '20px' }}>
                            <Button variant="contained" onClick={onClickEditor} startIcon={<NoteAltIcon />}>
                                Release Notes Editor
                            </Button>
                        </div>
                        <DataGrid
                            autoHeight
                            sx={{
                                '.MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#003883',
                                    color: '#fff',
                                },
                                '.MuiDataGrid-menuIcon .MuiSvgIcon-root': {
                                    color: '#fff',
                                },
                                '.MuiDataGrid-iconButtonContainer .MuiSvgIcon-root': {
                                    color: '#fff',
                                },
                            }}
                            columns={columns}
                            rows={paginationModel.data}
                            initialState={{
                                pagination: {
                                    pageSize: 10,
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Dialog open={editorOpen} onClose={handleEditorClose} maxWidth="md" fullWidth>
                <DialogTitle>Edit Release Notes</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={12} mt={1}>
                                <Autocomplete
                                    options={[
                                        { id: 1, label: 'Internal User' },
                                        { id: 2, label: 'External User' },
                                        { id: -1, label: 'Both' },
                                    ]}
                                    getOptionLabel={(option: any) => option.label}
                                    value={
                                        releaseNotesModel.userType !== null
                                            ? { id: releaseNotesModel.userType, label: getUserTypeText(releaseNotesModel.userType) }
                                            : null
                                    }
                                    onChange={(event, newValue: any) => {
                                        if (newValue && typeof newValue === 'object' && 'id' in newValue) {
                                            setReleaseNotesModel((prev) => ({ ...prev, userType: newValue.id }));
                                            setValidationErrors((prev) => ({ ...prev, userType: false }));
                                        } else {
                                            setReleaseNotesModel((prev) => ({ ...prev, userType: "" }));
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select the user type"
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            required
                                            error={validationErrors.userType}
                                            helperText={validationErrors.userType ? "User type is required" : ""}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6} sm={6} md={6}>
                                <TextField
                                    label="Title"
                                    variant="outlined"
                                    value={releaseNotesModel.title}
                                    onChange={(e) => {
                                        setReleaseNotesModel((prev) => ({ ...prev, title: e.target.value }));
                                        if (e.target.value.trim()) {
                                            setValidationErrors((prev) => ({ ...prev, title: false }));
                                        }
                                    }}
                                    fullWidth
                                    size="small"
                                    required
                                    error={validationErrors.title}
                                    helperText={validationErrors.title ? "Title is required" : ""}
                                />
                            </Grid>
                            <Grid item xs={6} sm={6} md={6}>
                                <TextField
                                    label="Message"
                                    variant="outlined"
                                    value={releaseNotesModel.message}
                                    onChange={(e) => {
                                        setReleaseNotesModel((prev) => ({ ...prev, message: e.target.value }));
                                        if (e.target.value.trim()) {
                                            setValidationErrors((prev) => ({ ...prev, message: false }));
                                        }
                                    }}
                                    fullWidth
                                    size="small"
                                    required
                                    error={validationErrors.message}
                                    helperText={validationErrors.message ? "Message is required" : ""}
                                />
                            </Grid>
                            <Grid item xs={6} sm={6} md={6}>
                                <TextField
                                    label="Version No"
                                    variant="outlined"
                                    value={releaseNotesModel.versionNo}
                                    onChange={(e) => {
                                        setReleaseNotesModel((prev) => ({ ...prev, versionNo: e.target.value }));
                                        if (e.target.value.trim()) {
                                            setValidationErrors((prev) => ({ ...prev, versionNo: false }));
                                        }
                                    }}
                                    fullWidth
                                    size="small"
                                    required
                                    error={validationErrors.versionNo}
                                    helperText={validationErrors.versionNo ? "Version No is required" : ""}
                                />
                            </Grid>
                            <Grid item xs={6} sm={6} md={6}>
                                <TextField
                                    id="date"
                                    type="date"
                                    fullWidth
                                    label="Date of Release"
                                    sx={{
                                        '.MuiOutlinedInput-root': {
                                            fontSize: '12px'
                                        },
                                        width: '100%'
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    size="small"
                                    required
                                    value={releaseNotesModel.releaseDate || ""}
                                    onChange={(e) => {
                                        setReleaseNotesModel((prev) => ({
                                            ...prev,
                                            releaseDate: e.target.value.trim(),
                                        }));
                                        if (e.target.value.trim()) {
                                            setValidationErrors((prev) => ({ ...prev, releaseDate: false }));
                                        }
                                    }}
                                    error={validationErrors.releaseDate} 
                                    helperText={validationErrors.releaseDate ? "Date of Release is required" : ""}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <Autocomplete
                                    multiple
                                    options={labelOptions}
                                    value={releaseNotesModel.labelName ? releaseNotesModel.labelName.split(',') : []}
                                    onChange={(event, newValue) => setReleaseNotesModel((prev) => ({ ...prev, labelName: newValue.toString() }))}
                                    renderTags={(value: string[], getTagProps) =>
                                        value.map((option: string, index: number) => (
                                            <Chip
                                                key={option}
                                                label={option}
                                                {...getTagProps({ index })}
                                                color="primary"
                                                onDelete={() => {
                                                    const updatedValue = releaseNotesModel.labelName.split(',').filter((item) => item !== option);
                                                    setReleaseNotesModel((prev) => ({ ...prev, labelName: updatedValue.toString() }))
                                                }}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Label Name"
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <div className="custom-quill-editor" id="textEditorId">
                                    <Editor
                                        releaseNotesModel={releaseNotesModel}
                                        setReleaseNotesModel={setReleaseNotesModel}
                                    />
                                </div>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="error" onClick={handleEditorClose} startIcon={<CancelOutlinedIcon />}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={() => handleAddReleaseNotes("save")} startIcon={<MailOutlinedIcon />}>
                        Save
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleAddReleaseNotes("publish")}
                        startIcon={<PublishIcon />}
                        disabled={
                            !releaseNotesModel.releaseDate ||
                            releaseNotesModel.releaseDate === "Invalid date"
                        }
                    >
                        Publish
                    </Button>
                </DialogActions>
            </Dialog>
            <Stack sx={{ width: '100%', marginTop: 2 }} spacing={2}>
                <Snackbar open={publishNotificationStatus} autoHideDuration={6000} onClose={() => setPublishNotificationStatus(false)}>
                    <Alert severity="success" onClose={() => setPublishNotificationStatus(false)}><b>Published Successfully!</b></Alert>
                </Snackbar>
                <Snackbar open={saveNotificationStatus} autoHideDuration={6000} onClose={() => setSaveNotificationStatus(false)}>
                    <Alert severity="success" onClose={() => setSaveNotificationStatus(false)}><b>Saved Successfully!</b></Alert>
                </Snackbar>
                <Snackbar open={error} autoHideDuration={6000} onClose={() => setError(false)}>
                    <Alert severity="error" onClose={() => setError(false)}><b>{errorMessage}</b></Alert>
                </Snackbar>
                <Snackbar open={successRequest} autoHideDuration={6000} onClose={() => setSuccessRequest(false)}>
                    <Alert severity="success" onClose={() => setSuccessRequest(false)}><b>{successMessage}</b></Alert>
                </Snackbar>
            </Stack>
        </div>
    );
};

